import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';

type HelpTopic = {
  id: string;
  labelEn: string;
  labelEs: string;
  summaryEn: string;
  summaryEs: string;
};

/** Keep ids in sync with CompliDesk `lib/helpTopics.js` (NextCompli). */
export const NEXTCOMPLI_HELP_TOPICS: HelpTopic[] = [
  {
    id: 'login',
    labelEn: 'Sign-in or account access',
    labelEs: 'Inicio de sesión o acceso a la cuenta',
    summaryEn: 'Cannot log in, MFA, or session problems.',
    summaryEs: 'No puede iniciar sesión, MFA o problemas de sesión.',
  },
  {
    id: 'alerts',
    labelEn: 'Alerts and notifications',
    labelEs: 'Alertas y notificaciones',
    summaryEn: 'Missing, noisy, or wrong alert delivery.',
    summaryEs: 'Alertas faltantes, excesivas o incorrectas.',
  },
  {
    id: 'data',
    labelEn: 'Screening or data results',
    labelEs: 'Resultados de cribado o datos',
    summaryEn: 'Unexpected matches, missing records, or stale lists.',
    summaryEs: 'Coincidencias inesperadas, registros faltantes o listas desactualizadas.',
  },
  {
    id: 'integration',
    labelEn: 'API or integration',
    labelEs: 'API o integración',
    summaryEn: 'Developer docs, keys, webhooks, or partner connectors.',
    summaryEs: 'Documentación, claves, webhooks o conectores.',
  },
  {
    id: 'billing',
    labelEn: 'Billing or subscription',
    labelEs: 'Facturación o suscripción',
    summaryEn: 'Invoices, renewals, plan changes, wallet usage.',
    summaryEs: 'Facturas, renovaciones, cambios de plan o uso de billetera.',
  },
  {
    id: 'known-issues',
    labelEn: 'Known issue / product bug',
    labelEs: 'Problema conocido / error del producto',
    summaryEn: 'Report a regression or ask for a workaround.',
    summaryEs: 'Informe una regresión o pida una alternativa.',
  },
  {
    id: 'documentation',
    labelEn: 'Help & documentation',
    labelEs: 'Ayuda y documentación',
    summaryEn: 'Missing or unclear docs; request a guide.',
    summaryEs: 'Documentación faltante o poco clara; solicite una guía.',
  },
  {
    id: 'success-plans',
    labelEn: 'Success Plans / professional services',
    labelEs: 'Planes de éxito / servicios profesionales',
    summaryEn: 'Ask for implementation help or a success plan.',
    summaryEs: 'Pida ayuda de implementación o un plan de éxito.',
  },
  {
    id: 'other',
    labelEn: 'Something else',
    labelEs: 'Otro asunto',
    summaryEn: 'General support request.',
    summaryEs: 'Solicitud general de soporte.',
  },
];

const inferGatewayBaseUrl = (): string => {
  const configured = (process.env.REACT_APP_GATEWAY_URL || '').trim();
  if (configured) {
    if (/^https?:\/\/localhost(?::|\/|$)/i.test(configured)) {
      return configured.replace(/localhost/i, '127.0.0.1').replace(/\/$/, '');
    }
    return configured.replace(/\/$/, '');
  }
  if (typeof window === 'undefined') {
    return 'http://127.0.0.1:8000';
  }
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1' || host === '::1') {
    return 'http://127.0.0.1:8000';
  }
  return `https://${host.replace(/^www\./, '').replace(/^dashboard\./, '').replace(/^metrics\./, '')}`;
};

type HelpCenterPageProps = {
  language?: 'en' | 'es';
  contactPath?: string;
  plansPath?: string;
};

export const HelpCenterPage: React.FC<HelpCenterPageProps> = ({
  language: languageProp,
  contactPath = '/contact-us',
  plansPath = '/plans',
}) => {
  const [params, setParams] = useSearchParams();
  const language =
    languageProp
    || (typeof document !== 'undefined' && document.documentElement.lang === 'es' ? 'es' : 'en');
  const isEs = language === 'es';

  const topicFromUrl = String(params.get('topic') || '').trim();
  const [topicId, setTopicId] = React.useState(
    NEXTCOMPLI_HELP_TOPICS.some((row) => row.id === topicFromUrl) ? topicFromUrl : 'other',
  );
  const [fullName, setFullName] = React.useState('');
  const [workEmail, setWorkEmail] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [status, setStatus] = React.useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [ticketNumber, setTicketNumber] = React.useState('');
  const [errorText, setErrorText] = React.useState('');

  React.useEffect(() => {
    if (NEXTCOMPLI_HELP_TOPICS.some((row) => row.id === topicFromUrl)) {
      setTopicId(topicFromUrl);
    }
  }, [topicFromUrl]);

  const selected = NEXTCOMPLI_HELP_TOPICS.find((row) => row.id === topicId) || NEXTCOMPLI_HELP_TOPICS[NEXTCOMPLI_HELP_TOPICS.length - 1];

  const selectTopic = (id: string) => {
    setTopicId(id);
    const next = new URLSearchParams(params);
    next.set('topic', id);
    setParams(next, { replace: true });
    setStatus('idle');
    setTicketNumber('');
    setErrorText('');
  };

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!workEmail.trim() || !message.trim()) {
      setErrorText(isEs ? 'Correo y mensaje son obligatorios.' : 'Email and message are required.');
      setStatus('error');
      return;
    }
    setStatus('sending');
    setErrorText('');
    try {
      const response = await fetch(`${inferGatewayBaseUrl()}/api/v1/help/ticket`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          topicId: selected.id,
          topicLabel: isEs ? selected.labelEs : selected.labelEn,
          fullName: fullName.trim(),
          workEmail: workEmail.trim(),
          message: message.trim(),
          page_path: typeof window !== 'undefined' ? window.location.pathname : '/help',
          site: 'nextcompli',
        }),
      });
      const body = (await response.json().catch(() => ({}))) as {
        desk?: { ticket?: string; delivery?: string; skipped?: boolean };
        error?: string;
      };
      if (!response.ok) {
        throw new Error(body.error || (isEs ? 'No se pudo abrir el caso.' : 'Could not open the case.'));
      }
      const number = body.desk?.ticket ? String(body.desk.ticket) : '';
      setTicketNumber(number);
      setStatus('sent');
      setMessage('');
    } catch (err) {
      setStatus('error');
      setErrorText(err instanceof Error ? err.message : (isEs ? 'No se pudo abrir el caso.' : 'Could not open the case.'));
    }
  };

  return (
    <section className="page-section contact-help-page help-center-page" aria-label={isEs ? 'Centro de ayuda' : 'Help center'}>
      <div className="contact-help-hero help-center-hero">
        <h1>{isEs ? 'Centro de ayuda empresarial' : 'Enterprise help center'}</h1>
        <p>
          {isEs
            ? 'Elija un tema del menú de ayuda. Abrimos un ticket en CompliDesk para el equipo de soporte.'
            : 'Pick a help-menu topic. We open a CompliDesk ticket for the support team.'}
        </p>
      </div>

      <div className="help-center-layout">
        <nav className="help-center-topics" aria-label={isEs ? 'Temas de ayuda' : 'Help topics'}>
          <ul>
            {NEXTCOMPLI_HELP_TOPICS.map((topic) => (
              <li key={topic.id}>
                <button
                  type="button"
                  className={topic.id === selected.id ? 'is-active' : undefined}
                  onClick={() => selectTopic(topic.id)}
                >
                  <strong>{isEs ? topic.labelEs : topic.labelEn}</strong>
                  <span>{isEs ? topic.summaryEs : topic.summaryEn}</span>
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <form className="help-center-form inquiry-form" onSubmit={onSubmit}>
          <h2>{isEs ? selected.labelEs : selected.labelEn}</h2>
          <p className="fine">{isEs ? selected.summaryEs : selected.summaryEn}</p>
          <label>
            {isEs ? 'Nombre' : 'Full name'}
            <input
              name="full_name"
              autoComplete="name"
              value={fullName}
              onChange={(event) => setFullName(event.target.value)}
            />
          </label>
          <label>
            {isEs ? 'Correo de trabajo' : 'Work email'}
            <input
              name="work_email"
              type="email"
              autoComplete="email"
              required
              value={workEmail}
              onChange={(event) => setWorkEmail(event.target.value)}
            />
          </label>
          <label>
            {isEs ? 'Describa el problema' : 'Describe the issue'}
            <textarea
              name="message"
              required
              rows={6}
              value={message}
              onChange={(event) => setMessage(event.target.value)}
            />
          </label>
          <button className="btn btn-primary" type="submit" disabled={status === 'sending'}>
            {status === 'sending'
              ? (isEs ? 'Enviando…' : 'Sending…')
              : (isEs ? 'Abrir ticket de ayuda' : 'Open help ticket')}
          </button>
          {status === 'sent' ? (
            <p className="fine" role="status">
              {isEs ? 'Caso recibido' : 'Case received'}
              {ticketNumber ? ` (${ticketNumber})` : ''}.{' '}
              {isEs ? 'El equipo de soporte responderá por correo.' : 'Support will reply by email.'}
            </p>
          ) : null}
          {status === 'error' && errorText ? (
            <p className="fine" role="alert">
              {errorText}
            </p>
          ) : null}
          <p className="fine help-center-links">
            <Link to={contactPath}>{isEs ? 'Contacto' : 'Contact'}</Link>
            {' · '}
            <Link to={plansPath}>{isEs ? 'Planes y precios' : 'Plans and pricing'}</Link>
          </p>
        </form>
      </div>
    </section>
  );
};
